import { z } from "zod";

/**
 * The launch video a new account is given, described as data.
 *
 * A model writes this and the renderer draws it, and the split between those
 * two jobs is the whole design. The model is a language model — it can decide
 * what the video says, which of three looks suits the product, and which word
 * in a line carries the weight. It cannot draw a frame. So everything about
 * *how* the video looks lives in the compositions, and everything the model is
 * allowed to decide lives in this schema.
 *
 * The practical consequence: a bad model response produces a video with weak
 * copy. It cannot produce a broken video, because there is no path from this
 * object to the drawing code that isn't a string in a slot.
 */

/**
 * The three looks, each a complete visual world rather than a colour swap.
 *
 * The model picks one from reading the site. A developer-tools product and a
 * design product want genuinely different films, and picking for them is a
 * judgement about the product — which is the kind of thing the model reading
 * the page is actually good at.
 */
export const launchStyles = ["neon", "soft", "terminal"] as const;
export type LaunchStyle = (typeof launchStyles)[number];

/** What each style is for, in the words the model is given to choose by. */
export const launchStyleBriefs: Record<LaunchStyle, string> = {
  neon: "Pure black, neon lime, warm light rising from the bottom edge. High contrast and kinetic. Suits developer tools, infrastructure, security, anything technical that wants to feel fast and modern.",
  soft: "Warm off-white with drifting peach, mint and lavender. Glass pills, teal accent, calm and airy. Suits design, collaboration, wellbeing, education — anything whose promise is that work gets gentler.",
  terminal:
    "Near-black with a coral sweep and monospace headlines. Precise and dense. Suits APIs, CLIs, data and analytics — anything whose users live in a terminal or a dashboard.",
};

/**
 * A line, and the one span inside it that gets the accent colour.
 *
 * Kinetic typography lives or dies on emphasis: "Still *editing* demos by
 * hand?" reads as a sentence with a point, and the same line set flat reads as
 * a caption. Asking for the line and the word separately — rather than letting
 * the model emit markup — means there is no parser to fool and no way for a
 * stray asterisk to reach the screen.
 *
 * `accent` must actually occur in `text`; `splitAccent` is what enforces that
 * at draw time, and it degrades to an unaccented line rather than throwing.
 */
export const launchLineSchema = z.object({
  text: z.string().min(1).max(90),
  accent: z.string().min(1).max(40).optional(),
});
export type LaunchLine = z.infer<typeof launchLineSchema>;

/**
 * A line split into before / accent / after.
 *
 * Case-insensitive, first occurrence, and a miss returns the whole line as
 * `before`. A model asked for a substring will occasionally hand back a
 * paraphrase of one, and a video that loses an emphasis is fine where a video
 * that fails to render is not.
 */
export function splitAccent(line: LaunchLine): { before: string; accent: string; after: string } {
  if (!line.accent) return { before: line.text, accent: "", after: "" };
  const at = line.text.toLowerCase().indexOf(line.accent.toLowerCase());
  if (at < 0) return { before: line.text, accent: "", after: "" };
  return {
    before: line.text.slice(0, at),
    // From the line, not from `accent` — so the casing shown is the casing written.
    accent: line.text.slice(at, at + line.accent.length),
    after: line.text.slice(at + line.accent.length),
  };
}

export const launchBriefSchema = z.object({
  style: z.enum(launchStyles),

  /** Who the video is about. `name` and `url` come from the scrape, not the model. */
  product: z.object({
    name: z.string().min(1).max(40),
    tagline: z.string().min(1).max(70),
    url: z.string().min(1).max(60),
  }),

  /** 0–8s. One or two lines, large, the whole frame. */
  hook: z.array(launchLineSchema).min(1).max(2),

  /**
   * 8–17s. Exactly three, because the section is built as three beats and a
   * variable count would make the timing a function of the model's output.
   */
  problem: z.array(launchLineSchema).length(3),

  /** 17–24s. The turn: one line, the pivot from pain to product. */
  turn: launchLineSchema,

  /** 24–36s. Three steps, each a label and a short caption beneath it. */
  steps: z.array(z.object({ label: z.string().min(1).max(24), caption: z.string().min(1).max(60) })).length(3),

  /**
   * 36–48s. Three proof points.
   *
   * `stat` is deliberately a string and deliberately optional. A number invites
   * the model to invent one, and an invented metric in a launch video is the
   * kind of lie that reaches customers. Absent, the point is drawn as a claim
   * rather than as a counter.
   */
  proof: z.array(z.object({ stat: z.string().min(1).max(12).optional(), label: z.string().min(1).max(44) })).length(3),

  /** 48–50s. The lockup line under the product name. */
  close: z.string().min(1).max(50),
});

export type LaunchBrief = z.infer<typeof launchBriefSchema>;

/**
 * The film's shape, in seconds.
 *
 * Fixed, and shared by the schema above and every composition below. The
 * sections tile the whole duration with no gap and no overlap — a gap is two
 * seconds of empty frame nobody asked for, and an overlap is two sections
 * drawing at once.
 */
export const launchDurationSeconds = 50;
export const launchFps = 60;

export const launchSections = [
  { key: "hook", start: 0, end: 8 },
  { key: "problem", start: 8, end: 17 },
  { key: "turn", start: 17, end: 24 },
  { key: "steps", start: 24, end: 36 },
  { key: "proof", start: 36, end: 48 },
  { key: "close", start: 48, end: 50 },
] as const;

export type LaunchSectionKey = (typeof launchSections)[number]["key"];

/** A section's place on the timeline, in frames. */
export function launchSectionFrames(
  key: LaunchSectionKey,
  fps: number = launchFps,
): { from: number; durationInFrames: number } {
  const section = launchSections.find((one) => one.key === key);
  if (!section) throw new Error(`no launch section named ${key}`);
  return { from: Math.round(section.start * fps), durationInFrames: Math.round((section.end - section.start) * fps) };
}

/**
 * The sections placed by the voice instead of by the fixed table: seven
 * boundaries in seconds, from `sectionBeats`. Frame edges are rounded once
 * and shared, so the sections still tile with no gap and no overlap.
 */
export function launchSectionFramesFrom(
  beats: readonly number[],
  fps: number = launchFps,
): { key: LaunchSectionKey; from: number; durationInFrames: number }[] {
  const edges = beats.map((seconds) => Math.round(seconds * fps));
  return launchSections.map((section, index) => ({
    key: section.key,
    from: edges[index]!,
    durationInFrames: edges[index + 1]! - edges[index]!,
  }));
}
